import { EVENTS } from '@bs/constants';

/**
 * A class for handling header dropdown functionality
 */
export default class Header {
  constructor() {
    this.element = document;
    this.lastScrollTop = 0;
    this.scrollThreshold = 10; // Minimum scroll distance to trigger hide/show
    this.init();
  }

  /**
   * Initialize header dropdowns
   */
  init() {
    this.cacheDomReferences();
    this.setupHandlers();
    this.enable();
  }

  /**
   * Cache DOM References for dropdowns
   */
  cacheDomReferences() {
    this.header = this.element.querySelector('header');
    this.headerMenuItems = this.element.querySelectorAll('.nav-item');
    this.mobileMenuToggle = this.element.querySelector('.mobile-menu-toggle');
    this.mainNavigation = this.element.querySelector('.main-navigation');
  }

  /**
   * Bind event handlers
   */
  setupHandlers() {
    this.handleMenuItemClick = this.handleMenuItemClickHandler.bind(this);
    this.handleClickOutside = this.handleClickOutsideHandler.bind(this);
    this.handleMobileMenuToggle = this.handleMobileMenuToggleHandler.bind(this);
    this.handleScroll = this.handleScrollHandler.bind(this);
  }

  /**
   * Set up dropdown event listeners
   */
  enable() {
    // Add click handlers for dropdown menu items
    this.headerMenuItems.forEach((menuItem) => {
      const link = menuItem.querySelector('.nav-link');
      if (link && menuItem.querySelector('.sub-menu')) {
        link.addEventListener(EVENTS.CLICK, (e) =>
          this.handleMenuItemClick(e, menuItem)
        );
      }
    });

    // Close dropdowns when clicking outside
    document.addEventListener(EVENTS.CLICK, this.handleClickOutside);

    // Add mobile menu toggle handler
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.addEventListener(
        EVENTS.CLICK,
        this.handleMobileMenuToggle
      );
    }

    // Add scroll handler for sticky header behavior
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  /**
   * Handle mobile menu toggle
   */
  handleMobileMenuToggleHandler() {
    const isExpanded =
      this.mobileMenuToggle.getAttribute('aria-expanded') === 'true';

    // Toggle mobile menu
    this.mobileMenuToggle.setAttribute(
      'aria-expanded',
      (!isExpanded).toString()
    );

    if (!isExpanded) {
      // Open mobile menu
      this.mainNavigation.classList.add('is-open');
      this.mobileMenuToggle.classList.add('is-active');
      // Prevent body scroll when nav is open
      document.body.style.overflow = 'hidden';
    } else {
      // Close mobile menu
      this.mainNavigation.classList.remove('is-open');
      this.mobileMenuToggle.classList.remove('is-active');
      // Restore body scroll when nav is closed
      document.body.style.overflow = '';
    }
  }

  /**
   * Handle dropdown menu item click
   * @param {Event} event - The click event
   * @param {HTMLElement} menuItem - The menu item element
   */
  handleMenuItemClickHandler(event, menuItem) {
    event.preventDefault();
    const subMenu = menuItem.querySelector('.sub-menu');
    const link = menuItem.querySelector('.nav-link');
    if (!subMenu || !link) return;
    // Close other dropdowns
    this.headerMenuItems.forEach((item) => {
      if (item !== menuItem) {
        const otherSubMenu = item.querySelector('.sub-menu');
        const otherLink = item.querySelector('.nav-link');
        if (otherSubMenu && otherLink) {
          otherSubMenu.classList.remove('show');
          otherLink.setAttribute('aria-expanded', 'false');
        }
      }
    });

    // Toggle current dropdown
    const isOpen = subMenu.classList.contains('show');
    subMenu.classList.toggle('show');
    link.setAttribute('aria-expanded', (!isOpen).toString());
  }

  /**
   * Handle clicks outside dropdowns to close them
   * @param {Event} event - The click event
   */
  handleClickOutsideHandler(event) {
    if (!this.header.contains(event.target)) {
      this.closeAllSubmenus();
    }
  }

  /**
   * Close all open submenus
   */
  closeAllSubmenus() {
    this.headerMenuItems.forEach((menuItem) => {
      const subMenu = menuItem.querySelector('.sub-menu');
      const link = menuItem.querySelector('.nav-link');
      if (subMenu && link) {
        subMenu.classList.remove('show');
        link.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /**
   * Handle scroll events for sticky header behavior
   */
  handleScrollHandler() {
    const currentScrollTop =
      window.pageYOffset || document.documentElement.scrollTop;

    const headerOffset = 500; // adjust this to the offset you want

    // Close any open submenus when scrolling
    this.closeAllSubmenus();

    // Don't hide header until after offset
    if (currentScrollTop <= headerOffset) {
      this.header.classList.remove('header-hidden');
      this.lastScrollTop = currentScrollTop;
      return;
    }

    // Only trigger if scroll distance exceeds threshold
    if (
      Math.abs(currentScrollTop - this.lastScrollTop) < this.scrollThreshold
    ) {
      return;
    }

    // Hide header when scrolling down, show when scrolling up
    if (currentScrollTop > this.lastScrollTop) {
      // Scrolling down - hide header
      this.header.classList.add('header-hidden');
    } else {
      // Scrolling up - show header
      this.header.classList.remove('header-hidden');
    }

    this.lastScrollTop = currentScrollTop;
  }
}
